import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const NewsletterSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const content = {
    en: {
      title: "Stay Ahead of the Market",
      subtitle: "Get exclusive insights, portfolio updates, and market intelligence delivered weekly",
      emailPlaceholder: "Enter your email address",
      subscribe: "Subscribe Now",
      subscribing: "Subscribing...",
      successTitle: "Welcome to REXFEL Insights!",
      successMessage: "You\'ll receive our next market intelligence report within 24 hours.",
      features: [
        "Weekly market analysis and trends",
        "Exclusive portfolio company updates",
        "Early access to investment opportunities",
        "Regulatory updates and compliance news"
      ],
      preview: {
        title: "This Week\'s Highlights",
        items: [
          "FinTech funding in Europe is recovering",
          "AOGX token performance analysis",
          "Portfolio spotlight: Mobellex expansion",
          "Regulatory update: EU Digital Services Act"
        ]
      }
    },
    de: {
      title: "Bleiben Sie dem Markt voraus",
      subtitle: "Erhalten Sie exklusive Einblicke, Portfolio-Updates und Marktintelligenz wöchentlich geliefert",
      emailPlaceholder: "E-Mail-Adresse eingeben",
      subscribe: "Jetzt abonnieren",
      subscribing: "Abonnieren...",
      successTitle: "Willkommen bei REXFEL Insights!",
      successMessage: "Sie erhalten unseren nächsten Marktintelligenz-Bericht innerhalb von 24 Stunden.",
      features: [
        "Wöchentliche Marktanalyse und Trends",
        "Exklusive Portfolio-Unternehmen-Updates",
        "Früher Zugang zu Investitionsmöglichkeiten",
        "Regulatorische Updates und Compliance-News"
      ],
      preview: {
        title: "Highlights dieser Woche",
        items: [
          "FinTech-Finanzierung in Europa erholt sich",
          "AOGX Token Performance-Analyse",
          "Portfolio-Spotlight: Mobellex Expansion",
          "Regulatorisches Update: EU Digital Services Act"
        ]
      }
    },
    fr: {
      title: "Gardez une longueur d\'avance sur le marché",
      subtitle: "Recevez des insights exclusifs, des mises à jour de portfolio et de l'intelligence de marché chaque semaine",
      emailPlaceholder: "Entrez votre adresse e-mail",
      subscribe: "S\'abonner maintenant",
      subscribing: "Abonnement...",
      successTitle: "Bienvenue dans REXFEL Insights!",
      successMessage: "Vous recevrez notre prochain rapport d\'intelligence de marché dans les 24 heures.",
      features: [
        "Analyse de marché et tendances hebdomadaires",
        "Mises à jour exclusives des entreprises du portfolio",
        "Accès anticipé aux opportunités d\'investissement",
        "Mises à jour réglementaires et actualités de conformité"
      ],
      preview: {
        title: "Points forts de cette semaine",
        items: [
          "Le financement FinTech en Europe se redresse",
          "Analyse de performance du token AOGX",
          "Spotlight portfolio: expansion Mobellex",
          "Mise à jour réglementaire: EU Digital Services Act"
        ]
      }
    },
    es: {
      title: "Mantente por delante del mercado",
      subtitle: "Recibe insights exclusivos, actualizaciones de portfolio e inteligencia de mercado semanalmente",
      emailPlaceholder: "Ingresa tu dirección de email",
      subscribe: "Suscribirse ahora",
      subscribing: "Suscribiendo...",
      successTitle: "¡Bienvenido a REXFEL Insights!",
      successMessage: "Recibirás nuestro próximo reporte de inteligencia de mercado en 24 horas.",
      features: [
        "Análisis de mercado y tendencias semanales",
        "Actualizaciones exclusivas de empresas del portfolio",
        "Acceso temprano a oportunidades de inversión",
        "Actualizaciones regulatorias y noticias de cumplimiento"
      ],
      preview: {
        title: "Destacados de esta semana",
        items: [
          "La financiación FinTech en Europa se está recuperando",
          "Análisis de rendimiento del token AOGX",
          "Spotlight del portfolio: expansión Mobellex",
          "Actualización regulatoria: EU Digital Services Act"
        ]
      }
    }
  };

  const currentContent = content?.[currentLanguage];

  const handleSubscribe = async (e) => {
    e?.preventDefault();
    if (!email) return;

    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubscribed(true);
      setIsLoading(false);
      setEmail('');
    }, 2000);
  };

  if (isSubscribed) {
    return (
      <section className="py-20 bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-background rounded-brand shadow-brand-lg p-12 border border-border">
            <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircle" size={40} className="text-success" />
            </div>
            
            <h3 className="text-2xl font-bold text-foreground mb-4">
              {currentContent?.successTitle}
            </h3>
            
            <p className="text-muted-foreground mb-8">
              {currentContent?.successMessage}
            </p>

            <Button
              variant="outline"
              onClick={() => setIsSubscribed(false)}
            >
              Subscribe Another Email
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-primary/5 to-accent/5">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Newsletter Signup */}
          <div className="space-y-8 scroll-reveal">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                {currentContent?.title}
              </h2>
              <p className="text-lg text-muted-foreground">
                {currentContent?.subtitle}
              </p>
            </div>

            {/* Subscription Form */}
            <form onSubmit={handleSubscribe} className="space-y-4">
              <Input
                type="email"
                placeholder={currentContent?.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                required
                className="text-lg"
              />
              
              <Button
                type="submit"
                variant="default"
                size="lg"
                loading={isLoading}
                disabled={!email || isLoading}
                fullWidth
                className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90"
                iconName="Mail"
                iconPosition="left"
              >
                {isLoading ? currentContent?.subscribing : currentContent?.subscribe}
              </Button>
            </form>

            {/* Features List */}
            <div className="space-y-3">
              {currentContent?.features?.map((feature, index) => (
                <div 
                  key={index}
                  className={`flex items-center space-x-3 stagger-${index + 1}`}
                >
                  <div className="w-5 h-5 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="Check" size={12} className="text-success" />
                  </div>
                  <span className="text-muted-foreground">{feature}</span>
                </div>
              ))}
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center space-x-6 pt-4 border-t border-border">
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="Shield" size={16} />
                <span>GDPR Compliant</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="Users" size={16} />
                <span>1150+ Subscribers</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="Clock" size={16} />
                <span>Weekly Updates</span>
              </div>
            </div>
          </div>

          {/* Newsletter Preview */}
          <div className="scroll-reveal">
            <div className="bg-background rounded-brand shadow-brand-lg border border-border overflow-hidden">
              {/* Preview Header */}
              <div className="bg-gradient-to-r from-primary to-accent p-6 text-white">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-white/20 rounded-brand-sm flex items-center justify-center">
                    <span className="font-bold text-sm">R</span>
                  </div>
                  <div>
                    <div className="font-bold">REXFEL Insights</div>
                    <div className="text-sm opacity-90">Weekly Market Intelligence</div>
                  </div>
                </div>
                
                <h3 className="text-xl font-bold">
                  {currentContent?.preview?.title}
                </h3>
              </div>

              {/* Preview Content */}
              <div className="p-6 space-y-4">
                {currentContent?.preview?.items?.map((item, index) => (
                  <div 
                    key={index}
                    className={`flex items-start space-x-3 p-3 rounded-brand-sm hover:bg-muted transition-brand stagger-${index + 1}`}
                  >
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </div>
                ))}

                {/* Preview Footer */}
                <div className="pt-4 border-t border-border">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Issue #247 • October 17, 2025</span>
                    <div className="flex items-center space-x-2">
                      <Icon name="Clock" size={12} />
                      <span>5 min read</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSection;